import { useEffect, useRef, useCallback, useState } from 'react';
import toast from 'react-hot-toast';

function VoiceSynthesizer({
  speakDependencies,
  isCountdownActive,
  checkingVisibility,
  isFullBodyVisible,
  missingPoints,
  lastMissingPoints,
  isTooClose,
  distanceStatus,
  poseChanged,
  countdown,
  initialCountdown,
  postureName,
  isPoseDetected,
  attempts,
  poseCaptureComplete,
  poseType,
  onPoseChangeDuringFinalCountdown,
  startCountdown
}) {
  const [voicesLoaded, setVoicesLoaded] = useState(false);
  const speechQueueRef = useRef([]);
  const isSpeakingRef = useRef(false);
  const lastSpokenMessage = useRef('');
  const lastNonCountdownSpeech = useRef(0);
  const voiceLoadAttempts = useRef(0);
  const wasFullBodyVisible = useRef(false);
  const lastDistanceWarningRef = useRef(0);
  const lastHoldMessageRef = useRef(0);
  const lastResetTimeRef = useRef(0);

  const speakMessage = useCallback(
    (message, isPriority = false, onEndCallback = null) => {
      if (!('speechSynthesis' in window)) {
        console.error('Speech synthesis not supported');
        toast.error('Speech synthesis not supported');
        return;
      }

      if (!voicesLoaded) {
        speechQueueRef.current.push({ message, isPriority, onEndCallback });
        console.log('Queuing message, voices not loaded:', message);
        return;
      }

      if (
        !isPriority &&
        (isSpeakingRef.current || (Date.now() - lastNonCountdownSpeech.current < 6000 && message === lastSpokenMessage.current))
      ) {
        console.log('Skipping message:', {
          message,
          isSpeaking: isSpeakingRef.current,
          isDuplicate: message === lastSpokenMessage.current
        });
        return;
      }

      try {
        if (isSpeakingRef.current && isPriority) {
          window.speechSynthesis.cancel();
          isSpeakingRef.current = false;
          console.log('Cancelled ongoing speech for priority message:', message);
        }

        const utterance = new SpeechSynthesisUtterance(message);
        utterance.lang = 'en-US';
        utterance.rate = 1.0;
        utterance.volume = 1.0;

        const voices = window.speechSynthesis.getVoices();
        const voice = voices.find((v) => v.lang === 'en-US') || voices[0];
        if (voice) utterance.voice = voice;

        utterance.onstart = () => {
          isSpeakingRef.current = true;
          console.log('Started speaking:', message);
        };

        utterance.onend = () => {
          isSpeakingRef.current = false;
          console.log('Finished speaking:', message);
          lastSpokenMessage.current = isPriority ? '' : message;
          if (!isPriority) lastNonCountdownSpeech.current = Date.now();
          if (onEndCallback) {
            console.log('Executing onEndCallback for message:', message);
            onEndCallback();
          }
          if (speechQueueRef.current.length > 0) {
            const next = speechQueueRef.current.shift();
            console.log('Processing next queued message:', next.message);
            setTimeout(() => speakMessage(next.message, next.isPriority, next.onEndCallback), 400);
          }
        };

        utterance.onerror = (event) => {
          isSpeakingRef.current = false;
          console.error('Speech error:', event);
          if (event.error !== 'interrupted') {
            toast.error('Speech failed: ' + event.error);
          } else {
            console.log('Speech interrupted, continuing with queue');
            if (speechQueueRef.current.length > 0) {
              const next = speechQueueRef.current.shift();
              console.log('Processing next queued message after interruption:', next.message);
              setTimeout(() => speakMessage(next.message, next.isPriority, next.onEndCallback), 400);
            }
          }
        };

        window.speechSynthesis.speak(utterance);
      } catch (err) {
        console.error('Speech synthesis error:', err);
        isSpeakingRef.current = false;
        toast.error('Speech synthesis error: ' + err.message);
      }
    },
    [voicesLoaded]
  );

  useEffect(() => {
    const loadVoices = () => {
      console.log('Attempting to load voices, attempt:', voiceLoadAttempts.current);
      const voices = window.speechSynthesis.getVoices();
      if (voices.length > 0) {
        setVoicesLoaded(true);
        console.log(
          'Voices loaded successfully:',
          voices.map((v) => v.name)
        );
        speechQueueRef.current.forEach(({ message, isPriority, onEndCallback }) => {
          console.log('Processing queued message:', message);
          speakMessage(message, isPriority, onEndCallback);
        });
        speechQueueRef.current = [];
      } else if (voiceLoadAttempts.current < 10) {
        voiceLoadAttempts.current += 1;
        console.log('No voices loaded, retrying...');
        setTimeout(loadVoices, 400);
      } else {
        console.error('Failed to load voices after 10 attempts');
        toast.error('Failed to load voices');
      }
    };

    loadVoices();
    window.speechSynthesis.onvoiceschanged = loadVoices;

    return () => {
      console.log('Cleaning up speech synthesis');
      window.speechSynthesis.cancel();
      window.speechSynthesis.onvoiceschanged = null;
    };
  }, [speakMessage]);

  // Handle full body detection with delayed countdown
  useEffect(() => {
    if (poseCaptureComplete) {
      console.log('Pose capture complete, speaking success message');
      speakMessage('Awesome pose, capturing completed', true);
      return;
    }

    if (initialCountdown !== null || countdown !== null || !checkingVisibility) {
      console.log('Countdown already active or checking disabled, skipping full body detection logic:', { initialCountdown, countdown, checkingVisibility });
      return;
    }

    if (isFullBodyVisible && !wasFullBodyVisible.current) {
      console.log('Full body detected, preparing to speak message');
      speakMessage('Now full body is detected, get ready with your pose in 7 seconds', true, () => {
        console.log('Finished speaking "Full body detected", calling startCountdown');
        if (typeof startCountdown.current === 'function') {
          console.log('startCountdown.current is a function, executing...');
          startCountdown.current();
        } else {
          console.error('startCountdown.current is not a function:', startCountdown.current);
          toast.error('Failed to start countdown');
        }
      });
      wasFullBodyVisible.current = true;
    } else if (!isFullBodyVisible && wasFullBodyVisible.current) {
      console.log('Full body not visible, cancelling countdown');
      speakMessage('Please ensure full body is visible', true);
      wasFullBodyVisible.current = false;
    }
  }, [isFullBodyVisible, initialCountdown, countdown, speakMessage, startCountdown, poseCaptureComplete]);

  // Handle distance and visibility warnings
  useEffect(() => {
    if (initialCountdown !== null || countdown !== null || !checkingVisibility) {
      console.log('Countdown active or checking disabled, skipping distance/visibility warnings');
      return;
    }

    const now = Date.now();
    const timeSinceLastWarning = now - lastDistanceWarningRef.current;

    if (!isFullBodyVisible && checkingVisibility && missingPoints.length > 0) {
      console.log('Speaking visibility warning:', missingPoints);
      speakMessage('Please adjust your position to show all body parts');
    } else if (isTooClose && timeSinceLastWarning > 5000) {
      console.log('Speaking too close warning');
      speakMessage('Move back, you are too close to the camera');
      lastDistanceWarningRef.current = now;
    } else if (distanceStatus === 'too far' && timeSinceLastWarning > 5000) {
      const isBent = poseType?.includes('bent') || poseType?.includes('Side');
      if (!isBent) {
        console.log('Speaking too far warning');
        speakMessage('Move closer, you are too far from the camera');
        lastDistanceWarningRef.current = now;
      }
    }
  }, [
    missingPoints,
    isTooClose,
    distanceStatus,
    isFullBodyVisible,
    checkingVisibility,
    initialCountdown,
    countdown,
    speakMessage,
    poseType
  ]);

  // Handle initial countdown speech
  useEffect(() => {
    if (initialCountdown !== null && initialCountdown >= 0) {
      console.log('Speaking initial countdown:', initialCountdown);
      if (initialCountdown > 0) {
        speakMessage(`${initialCountdown}`, true);
      } else if (initialCountdown === 0) {
        speakMessage('Great, now we are capturing your pose for another 3 seconds', true);
      }
    }
  }, [initialCountdown, speakMessage]);

  // Handle main countdown speech
  useEffect(() => {
    if (!isCountdownActive || countdown === null) {
      console.log('Main countdown speech skipped:', { isCountdownActive, countdown });
      return;
    }
    if (countdown <= 0) {
      console.log('Speaking capturing pose');
      speakMessage('Capturing pose', true);
    }
  }, [countdown, isCountdownActive, speakMessage]);

  // Handle pose change during final countdown
  useEffect(() => {
    if (poseChanged && countdown !== null && countdown <= 3 && countdown >= 1 && isCountdownActive) {
      const now = Date.now();
      if (now - lastHoldMessageRef.current > 2000) {
        console.log('Pose changed in final 3 seconds, triggering "Hold your pose" and reset');
        speakMessage('Hold your pose', true);
        lastHoldMessageRef.current = now;
        lastResetTimeRef.current = now;
        if (onPoseChangeDuringFinalCountdown) {
          console.log('Calling onPoseChangeDuringFinalCountdown');
          onPoseChangeDuringFinalCountdown();
        }
      }
    }
  }, [poseChanged, countdown, isCountdownActive, speakMessage, onPoseChangeDuringFinalCountdown]);

  // Handle attempt announcements
  useEffect(() => {
    if (attempts > 0 && attempts <= 3 && initialCountdown === null && countdown === null) {
      console.log('Speaking attempt announcement:', attempts);
      speakMessage(`Attempt ${attempts} of 3`, true);
    }
  }, [attempts, initialCountdown, countdown, speakMessage]);

  return null;
}

export default VoiceSynthesizer;
